import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DirSpecializationType, SpecializationDirActionResultType } from '@econtractor/types';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import CreatableNameSelect from 'components/forms/CreatableNameSelect';
import type { CreatableNameOption } from 'components/forms/CreatableNameSelect';
import InputLabel from 'components/others/InputLabel';
import * as apiSettings from 'api/apiSettings';
import * as apiUsers from 'api/apiUsers';
import type { UserTabProps } from '../../types';

const SPECIALIZATION_NAME_MAX_LENGTH = 50;

const UserSpecializationSelect = ({ user, dispatchUser }: UserTabProps) => {
    const { t } = useTranslation(['common']);
    const { localSettings, dispatchLocalSettings } = useSettings();
    const { showToast } = useToast();
    const value = useMemo(
        () => (Array.isArray(user?.specializationIds) ? user.specializationIds : []),
        [user?.specializationIds],
    );

    const options = useMemo(() => {
        const items = (localSettings?.DIRS?.Specialization || []) as DirSpecializationType[];
        const selected = new Set(value);
        return items
            .filter((item) => !item.hidden || selected.has(item.ID))
            .map((item) => ({ value: item.ID, label: item.specializationName }));
    }, [localSettings?.DIRS?.Specialization, value]);

    const setIds = (ids: number[]) => {
        dispatchUser({
            type: 'SET_GENERAL_FIELD',
            field: 'specializationIds',
            value: ids,
        });
    };

    const handleCreate = async (inputValue: string): Promise<boolean> => {
        const name = String(inputValue || '').trim();
        if (!name) return false;

        const res = (await apiUsers.createSpecialization(name)) as SpecializationDirActionResultType | null;
        if (res?.stat && res.id) {
            const settings = await apiSettings.getSettings();
            if (settings) dispatchLocalSettings({ type: 'SET', settings });
            setIds(value.includes(res.id) ? value : [...value, res.id]);
            return true;
        }
        if (res?.errCode === 'DUPLICATE_NAME') {
            const existing = options.find((opt) => opt.label.trim().toLowerCase() === name.toLowerCase());
            if (existing) {
                setIds(value.includes(existing.value) ? value : [...value, existing.value]);
                return true;
            }
            showToast(t('Users.specializationDuplicate'), 'danger');
            return false;
        }
        if (res?.errCode === 'EMPTY_NAME') {
            showToast(t('Users.specializationEmpty'), 'danger');
        } else if (res?.errCode === 'NAME_TOO_LONG') {
            showToast(t('Users.specializationTooLong'), 'danger');
        } else {
            showToast(t('common:messError'), 'danger');
        }
        return false;
    };

    const handleChange = (next: CreatableNameOption[]) => {
        setIds(next.map((opt) => opt.value));
    };

    return (
        <InputLabel title={t('Users.specialization')}>
            <CreatableNameSelect
                isMulti
                options={options}
                value={value}
                isClearable={false}
                placeholder={t('Users.specialization')}
                addLabel={t('Users.btnAdd')}
                requiredMessage={t('Users.specializationEmpty')}
                maxLength={SPECIALIZATION_NAME_MAX_LENGTH}
                formatCreateLabel={(input) => t('Users.createOption', { name: input })}
                onChange={handleChange}
                onCreate={handleCreate}
            />
        </InputLabel>
    );
};

export default UserSpecializationSelect;
